<template>
  <div class="my-tasks-card">
    <div class="card-header">
      <h2>My All Tasks</h2>
      <div class="actions">
        <button class="btn btn-secondary" @click="reload" :disabled="loading">
          <span class="icon">🔄</span> Refresh
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading your tasks...</p>
    </div>

    <div v-else-if="tasks.length > 0"
         class="tasks-table"
         :style="{ maxHeight: tableMaxHeight }">
      <div class="tasks-row tasks-header">
        <div>Task</div>
        <div>Workstream</div>
        <div>Assignees</div>
        <div>Due Date</div>
        <div>Status</div>
      </div>
      <div
        v-for="t in tasks"
        :key="t.id || t.task_id || t._id"
        :class="['tasks-row', 'clickable', { 'is-hovered': hoveredTaskId === (t.id || t.task_id || t._id) }]"
        role="button"
        tabindex="0"
        @mouseenter="hoveredTaskId = t.id || t.task_id || t._id"
        @mouseleave="hoveredTaskId = null"
        @click="navigateToTaskWorkstream(t)"
        @keydown.enter="navigateToTaskWorkstream(t)"
        @keydown.space.prevent="navigateToTaskWorkstream(t)"
      >
        <div class="cell-title">{{ t.title || 'Untitled' }}</div>
        <div>{{ t.workstream?.name || '—' }}</div>
        <div>{{ formatAssigneesNames(t) }}</div>
        <div>{{ formatDateShort(t.due_date || t.dueDate) }}</div>
        <div>{{ formatStatusLabel(t.status) }}</div>
      </div>
    </div>

    <div v-else class="empty-state">
      <div class="empty-icon">📋</div>
      <p>No tasks found.</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { tasksApi } from '@/services/api'

export default {
  name: 'MyTasksTable',
  props: {
    maxRows: { type: Number, default: 10 },
    rowHeight: { type: Number, default: 44 } // px (approx height per row)
  },
  setup(props) {
    const router = useRouter()
    const tasks = ref([])
    const loading = ref(false)
    const hoveredTaskId = ref(null)

    const tableMaxHeight = computed(() => `${(props.maxRows + 1) * props.rowHeight}px`) // +1 for header

    const reload = async () => {
      loading.value = true
      try {
        const list = await tasksApi.listMine()
        tasks.value = Array.isArray(list) ? list : (list?.tasks || [])
      } catch (e) {
        console.error('Failed to load my tasks', e)
        tasks.value = []
      } finally {
        loading.value = false
      }
    }

    const navigateToTaskWorkstream = (t) => {
      if (!t) return
      const wsId = t.workstream_id || t.workstreamId || (t.workstream && (t.workstream.id || t.workstream.workstream_id))
      if (wsId) {
        const taskId = t.id || t.task_id || t.taskId
        router.push({ name: 'workstream-detail', params: { id: wsId }, query: taskId ? { task: taskId } : {} })
      } else {
        alert('No workstream found for this task')
      }
    }

    const formatDateShort = (date) => {
      if (!date) return '—'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '—'
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return d.toLocaleDateString('en-US', options)
    }

    const formatAssignees = (arr) => Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '—'
    const formatAssigneesNames = (t) => {
      const details = Array.isArray(t?.assignees_details) ? t.assignees_details : []
      if (details.length > 0) {
        return details.map(d => d?.name || d?.email || d?.id).join(', ')
      }
      return formatAssignees(t?.assignees)
    }

    const formatStatusLabel = (status) => {
      const map = { in_progress: 'In Progress', done: 'Done', todo: 'Todo' }
      return map[status] || (status ? String(status) : '—')
    }

    onMounted(reload)

    return {
      tasks,
      loading,
      hoveredTaskId,
      tableMaxHeight,
      reload,
      navigateToTaskWorkstream,
      formatAssigneesNames,
      formatDateShort,
      formatStatusLabel
    }
  }
}
</script>

<style scoped>
.my-tasks-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.5rem;
}

.card-header h2 { margin: 0; font-size: 1.1rem; color: #2c3e50; }
.actions { display: inline-flex; gap: 0.5rem; }
.btn { padding: 0.4rem 0.8rem; border-radius: 8px; border: 1px solid #e5e7eb; background: #fff; cursor: pointer; }
.btn-secondary { color: #4b5563; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }

.loading-container { display: flex; flex-direction: column; align-items: center; padding: 1rem; color: #6c757d; }
.spinner { width: 28px; height: 28px; border: 3px solid #f3f3f3; border-top: 3px solid #667eea; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 0.5rem; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

/* Exact table layout from WorkstreamsView */
.tasks-table {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.5fr 1fr 1fr;
  gap: 0px;
  overflow-y: auto; /* make scrollable */
}
.tasks-row { display: contents; }
.tasks-row > div {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f3f5;
  color: #2c3e50;
  font-size: 0.95rem;
  transition: background-color 0.12s ease-in-out;
}
.tasks-row.clickable > div { cursor: pointer; }
.tasks-row.is-hovered > div { background: #f8f9fa; }
.tasks-header > div { font-weight: 600; color: #6c757d; background: #fafafa; }
.cell-title { font-weight: 600; }

.empty-state { text-align: center; color: #9ca3af; padding: 1rem; }
.empty-icon { font-size: 2rem; margin-bottom: 0.25rem; }
</style>
